/**
 * Sanitize a cell value for clipboard copy — strips HTML tags, normalizes whitespace.
 */
export function sanitizeForCopy(value) {
    return String(value ?? '')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/?[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Format a row as tab-separated string for spreadsheet paste.
 */
export function rowToTsv(cells) {
    return cells.map((c) => sanitizeForCopy(c)).join('\t');
}

/**
 * Build full TSV string (headers + rows) for clipboard.
 */
export function tableToTsv(headers, rows) {
    const headerLine = headers.map((h) => sanitizeForCopy(h)).join('\t');
    const dataLines = rows.map((row) => rowToTsv(row));
    return [headerLine, ...dataLines].join('\n');
}

/**
 * Build CSV content with BOM for proper UTF-8 in Excel.
 * Uses semicolon delimiter for better compatibility with Russian Excel.
 */
export function tableToCsv(headers, rows) {
    const escape = (v) => {
        const s = String(v ?? '');
        return s.includes(';') || s.includes('"') || s.includes('\n')
            ? '"' + s.replace(/"/g, '""') + '"'
            : s;
    };
    const lines = [headers, ...rows].map((row) => row.map(escape).join(';'));
    return '﻿' + lines.join('\n');
}

/**
 * Trigger a file download in the browser.
 */
export function downloadFile(filename, content, mime = 'text/csv;charset=utf-8;') {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Format number with locale separators.
 */
export function formatNumber(value) {
    if (value == null || value === '') return '-';
    const n = Number(value);
    if (isNaN(n)) return String(value);
    return n.toLocaleString('ru-RU');
}

/**
 * Check if a string looks like an image URL.
 */
export function isImageUrl(value) {
    if (typeof value !== 'string') return false;
    return /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|bmp)/i.test(value);
}

/**
 * Compute summary value for a numeric column.
 */
export function computeSummary(values, type) {
    const nums = values.map(Number).filter((n) => !isNaN(n));
    if (!nums.length) return '-';
    switch (type) {
        case 'sum':
            return nums.reduce((a, b) => a + b, 0);
        case 'avg':
            return nums.reduce((a, b) => a + b, 0) / nums.length;
        case 'min':
            return Math.min(...nums);
        case 'max':
            return Math.max(...nums);
        case 'count':
            return nums.length;
        default:
            return '-';
    }
}
